/**
 * @link      https://karmabunny.com.au
 * @copyright Copyright (c) 2023 Karmabunny
 */
import { ContactEntries, ContactEntry, CustomContent, CustomContentItem, Entity, Facility, Faq } from "../types";
import { API } from "./base";


const api = new API();


/**
 * Hits the `#[Route('{uid}')]` in the Entity controller in TheyStay/API returning
 * a list of all entities.
 */
export async function getAllEntities() {

    const res = await api.call({
        method: 'GET',
        endpoint: `/entities`,
    })

    return res.items as Entity[];
}

/**
 * Hits the `#[Route('{uid}')]` in the Entity controller in TheyStay/API returning
 * a single entity object.
 */
export async function getEntity(entity_uid?: string) {
    entity_uid ??= api.config.base_entity_uid;

    const res = await api.call({
        method: 'GET',
        endpoint: `/entities/${entity_uid}`,
    })

    return res as Entity;
}

/**
 * Hits the `#[Route('{uid}/faq')]` in the Entity controller in TheyStay/API returning
 * a list of faqs belonging to the provided entity.
 */
export async function getFaqs(entity_uid?: string) {
    entity_uid ??= api.config.base_entity_uid;

    const res = await api.call({
        method: 'GET',
        endpoint: `/entities/${entity_uid}/faq`,
    });
    return res.items as Faq[];
}

/**
 * Hits the `#[Route('{uid}/faq')]` in the Entity controller in TheyStay/API returning
 * a list of faqs belonging to the provided entity.
 */
export async function getFacilities(entity_uid?: string) {
    entity_uid ??= api.config.base_entity_uid;

    const res = await api.call({
        method: 'GET',
        endpoint: `/entities/${entity_uid}/facilities`,
    });
    return res.items as Facility[];
}

/**
 * Hits the `#[Route('{uid}/faq')]` in the Entity controller in TheyStay/API returning
 * a list of faqs belonging to the provided entity.
 */
export async function getFacility(deal_uid: string, entity_uid?: string) {
    entity_uid ??= api.config.base_entity_uid;

    const res = await api.call({
        method: 'GET',
        endpoint: `/entities/${entity_uid}/facilities/${deal_uid}`,
    });
    return res as Facility;
}

/**
 * Hits the `#[Route('{uid}/contact')]` in the Entity controller in TheyStay/API returning
 * a list of contact detail items as specified by the entity owner.
 */
export async function getContacts(entity_uid?: string) {
    entity_uid ??= api.config.base_entity_uid;

    const res = await api.call({
        method: 'GET',
        endpoint: `/entities/${entity_uid}/contact`,
    });
    return res as ContactEntries;
}

/**
 * Hits the `#[Route('{uid}/contact')]` in the Entity controller in TheyStay/API returning
 * a list of contact detail items as specified by the entity owner.
 */
export async function getContactItem(contact_uid: string, entity_uid?: string) {
    entity_uid ??= api.config.base_entity_uid;

    const res = await api.call({
        method: 'GET',
        endpoint: `/entities/${entity_uid}/content/${contact_uid}`,
    });
    return res as ContactEntry;
}

/**
 * Hits the `#[Route('{uid}/content')]` in the Entity controller in TheyStay/API returning
 * a list of custom content items as specified by the entity owner.
 */
export async function getCustomContent(entity_uid?: string) {
    entity_uid ??= api.config.base_entity_uid;

    const res = await api.call({
        method: 'GET',
        endpoint: `/entities/${entity_uid}/content`,
    });
    return res as CustomContent;
}

/**
 * Hits the `#[Route('{uid}/content/{uid}')]` in the Entity controller in TheyStay/API returning
 * a list of custom content items as specified by the entity owner.
 */
export async function getCustomContentItem(content_uid: string, entity_uid?: string) {
    entity_uid ??= api.config.base_entity_uid;

    const res = await api.call({
        method: 'GET',
        endpoint: `/entities/${entity_uid}/content/${content_uid}`,
    });
    return res as CustomContentItem;
}
